import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { Event } from '../../types';

interface EventViewDialogProps {
  open: boolean;
  event: Event | null;
  onClose: () => void;
}

const EventViewDialog: React.FC<EventViewDialogProps> = ({
  open,
  event,
  onClose,
}) => {
  if (!event) return null;

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? { bg: '#dcfce7', color: '#16a34a' }
      : { bg: '#fee2e2', color: '#dc2626' };
  };

  const statusStyle = getStatusColor(event.isActive);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EventIcon sx={{ color: '#3b82f6', fontSize: 28 }} />
          <Box>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Agenda Details
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6b7280',
                mt: 0.5,
              }}
            >
              View agenda information
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#6b7280',
            '&:hover': { bgcolor: '#f3f4f6' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Event Name and Status */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                mt: 1,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#111827',
                  flex: 1,
                }}
              >
                {event.eventName}
              </Typography>
              <Chip
                label={event.isActive ? 'Active' : 'Inactive'}
                sx={{
                  bgcolor: statusStyle.bg,
                  color: statusStyle.color,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  height: 32,
                  px: 1,
                }}
              />
            </Box>
          </Grid>

          {/* Event Code */}
          {event.eventCode && (
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#f9fafb',
                  borderRadius: 2,
                  border: '1px solid #e5e7eb',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#6b7280',
                    mb: 1,
                  }}
                >
                  Agenda Code
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#111827',
                    fontFamily: 'monospace',
                  }}
                >
                  {event.eventCode}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Registration Count */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                bgcolor: '#f0f9ff',
                borderRadius: 2,
                border: '1px solid #bae6fd',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#0369a1',
                  mb: 1,
                }}
              >
                Registrations
              </Typography>
              <Typography
                sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0c4a6e' }}
              >
                {event.registrations?.length || 0}
              </Typography>
            </Box>
          </Grid>

          {/* Description */}
          {event.description && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <DescriptionIcon
                  sx={{ color: '#6b7280', mt: 0.5, fontSize: 20 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#374151',
                      mb: 1,
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      lineHeight: 1.6,
                    }}
                  >
                    {event.description}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Venue */}
          {event.venue && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <LocationIcon
                  sx={{ color: '#6b7280', mt: 0.5, fontSize: 20 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#374151',
                      mb: 1,
                    }}
                  >
                    Venue
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {typeof event.venue === 'string'
                      ? event.venue
                      : event.venue?.venueName || '-'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Date Range */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <CalendarIcon sx={{ color: '#6b7280', mt: 0.5, fontSize: 20 }} />
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#374151',
                    mb: 1,
                  }}
                >
                  Agenda Dates
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {event.startDate && (
                    <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      <strong>Start:</strong> {formatDate(event.startDate)}
                    </Typography>
                  )}
                  {event.endDate && (
                    <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      <strong>End:</strong> {formatDate(event.endDate)}
                    </Typography>
                  )}
                  {event.startDate && event.endDate && (
                    <Typography
                      sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 1 }}
                    >
                      Duration:{' '}
                      {Math.ceil(
                        (new Date(event.endDate).getTime() -
                          new Date(event.startDate).getTime()) /
                          (1000 * 60 * 60 * 24)
                      )}{' '}
                      days
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          borderTop: '1px solid #e5e7eb',
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 4,
            py: 1.5,
            borderRadius: 2,
            bgcolor: '#3b82f6',
            '&:hover': {
              bgcolor: '#2563eb',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventViewDialog;
